/**
 * @fileoverview Renders a read-only meta field UI component for Form metadata.
 * Dispatched from `LayoutUI` for every `UIMetaField`.
 *
 * The wrapper carries `data-pdf-field-group`, which the PDF export stylesheet
 * turns into `break-inside: avoid`. It belongs on field-sized subtrees only —
 * on anything that can outgrow a page it forces a spurious page break
 * (SYD-2298).
 *
 * The same wrapper carries `data-form-field-key`, and a copy-only
 * `formFieldClipboard` registration answers under it: the form's right-click menu
 * copies the displayed string and leaves Paste Field disabled, because a meta
 * value is the platform's and never the filler's (SYD-2232). The key is namespaced
 * `meta.<id>` so a meta field and a form field of the same name stay distinct,
 * while the registration carries the bare `<id>` the copied envelope names.
 */

import { FC, memo, useEffect, useMemo, useRef } from 'react';
import { theme, Input } from 'antd';
import { useSnapshot } from 'valtio';
import {
    UIMetaField,
    AgentType,
    Meta
} from '#types/System.types';
import { MetaFieldUIDescription } from '#types/System.Parameters.types';
import { useUser } from '../../use/useUser';
import FlexCol from '../atoms/FlexCol';
import FieldLabel from '../atoms/FieldLabel';
import { formStore, useEntityRecordScope } from '../../store';
import { formFieldClipboard, type FieldClipboardTarget } from '../../store/form';

interface MetaFieldUIProps {
    /** Configuration object containing meta field properties */
    uiMetaField: UIMetaField;
}

/**
 * Formats a meta value based on its type for display
 */
const formatMetaValue = (value: any, _id: keyof Meta): string => {
    if (!value) return '';
    
    if (value.toDate) {
        return value.toDate().toLocaleString();
    }
    
    if (Object.values(AgentType).includes(value)) {
        return value.toString();
    }
    
    return value.toString();
};

/**
 * A read-only component that displays a single meta value from a Form.
 * 
 * @component
 * @memo
 */
const MetaFieldUI: FC<MetaFieldUIProps> = memo(({
    uiMetaField,
}) => {
    const { token } = theme.useToken();
    const { id } = uiMetaField;
    const entityScope = useEntityRecordScope();
    const formSnap = useSnapshot(formStore.state);
    /* Read unconditionally: a `useSnapshot` whose result is never read subscribes
     * to the whole proxy, so branching before this read made every MetaFieldUI on
     * an entity record re-render on any form write. */
    const formMetaValue = formSnap.formVersion.data?.meta?.[id];
    const metaValue: Meta[keyof Meta] | undefined = entityScope.insideEntityRecordContext
        ? entityScope.entityRecord.data?.meta?.[id]
        : formMetaValue;
    const formattedValue = formatMetaValue(metaValue, id);

    const { 
        user 
    } = useUser({ 
        userId: id === 'userId' ? metaValue : undefined,
        enabled: id === 'userId' && !!metaValue
    });

    const name = user?.data?.name ?? '';
    const email = user?.data?.email ?? '';

    const displayValue = id === 'userId' && name
        ? `${name}(${email})`
        : formattedValue;

    const label = id in MetaFieldUIDescription 
        ? MetaFieldUIDescription[id as keyof typeof MetaFieldUIDescription].shortLabel 
        : id;
    const tooltip = id in MetaFieldUIDescription
        ? MetaFieldUIDescription[id as keyof typeof MetaFieldUIDescription].longDescription
        : `${id} value`;

    /**
     * The document this meta value belongs to, on the entity-before-form precedence
     * `useFieldUI` resolves a field's path with. `null` outside both, where there is
     * nothing for the menu to name.
     */
    const path = entityScope.insideEntityRecordContext && entityScope.entityId && entityScope.recordId
        ? `entities/${entityScope.entityId}/records/${entityScope.recordId}`
        : formSnap.formId && formSnap.versionId
            ? `forms/${formSnap.formId}/formVersions/${formSnap.versionId}`
            : null;
    const fieldKey = path === null ? null : `${path}#meta.${id}`;

    const shownValueRef = useRef(displayValue);
    shownValueRef.current = displayValue;

    const clipboardTarget = useMemo<FieldClipboardTarget>(() => ({
        read: () => shownValueRef.current,
        fieldId: String(id),
        fieldType: undefined,
        write: null,
    }), [id]);

    useEffect(() => {
        if (fieldKey === null) return;
        return formFieldClipboard.registerTarget(fieldKey, clipboardTarget);
    }, [fieldKey, clipboardTarget]);

    return (
        <FlexCol
            gap={0}
            {...uiMetaField.props}
            data-pdf-field-group="true"
            data-form-field-key={fieldKey ?? undefined}
        >
            <FieldLabel
                label={label}
                tooltip={tooltip}
            />
            <Input 
                id={`meta-${id}`}
                value={displayValue} 
                readOnly 
                variant='filled' 
                style={{
                    cursor: 'default',
                    backgroundColor: token.colorBgTextActive,
                }}
            />
        </FlexCol>
    );
});

MetaFieldUI.displayName = 'MetaFieldUI';

export default MetaFieldUI; 